import React, { useState, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import CdcHeader from '../CdcHeader';
import CdcFooter from '../CdcFooter';
import Navbar from '../Navbar';
import SessionTimeout from '../SessionTimeout/SessionTimeout.js';
import SessionConstants from '../../config/SessionConstants';
import LoginValidator from '../LoginValidator';
import TabLock from '../../utils/TabLock';
import TabConflict from '../TabConflict';

import './index.css';
import StatusFeed from '../StatusFeed/index.js';

const AuthenticatedRoute = ({ children }) => {
    const navRef = useRef(null);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [fullscreen] = useState(JSON.parse(sessionStorage.getItem(SessionConstants.FULLSCREEN)));
    //  null = not yet checked, true = this tab owns the session, false = another tab does.
    //  Claiming here (rather than at login) also catches a tab that was duplicated *after*
    //  authenticating, which inherits a valid session but must not race the refresh token.
    const [tabOwned, setTabOwned] = useState(null);

    useEffect(() => {
        setTabOwned(TabLock.claimOwnership());
        //  If a sibling tab later takes over the lock (this tab was backgrounded/asleep long
        //  enough for its heartbeat to lapse past the TTL), drop out of the authenticated app
        //  and show the conflict screen instead of running on as an owner-less zombie tab that
        //  would race the single-use refresh token.
        const unsubscribe = TabLock.subscribeOwnershipLost(() => setTabOwned(false));
        return unsubscribe;
    }, []);

    const handleValidation = (authenticated) => {
        setIsAuthenticated(authenticated);
    };

    //  Wait until ownership is resolved before mounting anything that can trigger the
    //  auth/refresh flow. Rendering LoginValidator while tabOwned is still null would let
    //  a duplicate tab hit the single-use refresh token before the ownership check blocks it.
    if (tabOwned === null) {
        return <div>Loading...</div>;
    }

    if (tabOwned === false) {
        return <TabConflict />;
    }

    if (isAuthenticated === null) {
        // Optionally show a loading indicator while validating
        return (
            <LoginValidator onValidation={handleValidation}>
                <div>Loading...</div>
            </LoginValidator>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/landing" replace />;
    }

    return (
        <LoginValidator onValidation={handleValidation}>
            <nav aria-label='skip navigation'>
                <a
                    href="#skip-to-content"
                    className="skipNavLink"
                    onClick={e => {
                        e.preventDefault();
                        const el = document.getElementById('skip-to-content');
                        if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            el.focus?.();
                        }
                    }}
                >
                    Skip to content
                </a>
            </nav>
            <CdcHeader />
            <nav ref={navRef}><Navbar menuContainerRef={navRef} /></nav>
            <StatusFeed/>
            <SessionTimeout />
            <main className={fullscreen ? 'container-wide' : 'container'} id="main-content">
                <div id="skip-to-content" tabIndex={-1}></div>
                {children}
            </main>
            <CdcFooter />
        </LoginValidator>
    );
};

export default AuthenticatedRoute;